import { useCallback } from "react";
import { useApp } from "../state/store";
import { isPdfFile, isPngFile, loadPdf, loadPng, renderPdfPage } from "../domain/source";
import { useT } from "../i18n/store";
import type { Source } from "../domain/types";

export const useFileLoader = () => {
  const setSource = useApp((s) => s.setSource);
  const setSign = useApp((s) => s.setSign);
  const setBusy = useApp((s) => s.setBusy);
  const resetViewport = useApp((s) => s.resetViewport);
  const t = useT();

  const handleFile = useCallback(async (file: File) => {
    setBusy(t("busy.loading"));
    try {
      let source: Source;
      if (isPdfFile(file)) {
        const pdfDoc = await loadPdf(file);
        const rendered = await renderPdfPage(pdfDoc, 1);
        source = {
          type: "pdf",
          name: file.name,
          pdfDoc,
          ...rendered,
        };
      } else if (isPngFile(file)) {
        source = await loadPng(file);
      } else {
        alert(t("alert.bad"));
        return;
      }
      setSource(source);
      setSign({ width: source.nativeWidthMm, height: source.nativeHeightMm });
      resetViewport();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      alert(t("alert.openFail", { msg }));
    } finally {
      setBusy(null);
    }
  }, [setBusy, setSource, setSign, resetViewport, t]);

  const selectPdfPage = useCallback(async (pageNum: number) => {
    const state = useApp.getState();
    const source = state.source;
    if (!source || source.type !== "pdf") return;
    setBusy(t("busy.rendering"));
    try {
      const rendered = await renderPdfPage(source.pdfDoc, pageNum);
      setSource({
        type: "pdf",
        name: source.name,
        pdfDoc: source.pdfDoc,
        ...rendered,
      });
      setSign({ width: rendered.nativeWidthMm, height: rendered.nativeHeightMm });
      resetViewport();
    } finally {
      setBusy(null);
    }
  }, [setBusy, setSource, setSign, resetViewport, t]);

  return { handleFile, selectPdfPage };
};
